import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Cake as CakeIcon,
  Celebration as CelebrationIcon,
  Close as CloseIcon,
  EmojiEvents as GraduationIcon,
  Luggage as FieldTripIcon,
  Star as StarIcon,
  WbSunny as HolidayIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ParentPortalLayout from "../components/ParentPortalLayout";
import ParentPortalTablePreloader from "../components/ParentPortalPreloader";
import { parentPortalAPI } from "../utils/api";
import type { NurseryEvent, NurseryEventType } from "../types/parent";
import {
  parentPortalSolidButtonSx,
  parentPortalTheme,
} from "../theme/parentPortalTheme";

const eventStyles: Record<
  NurseryEventType,
  { color: string; icon: React.ReactNode }
> = {
  birthday: {
    color: parentPortalTheme.eventTypes.birthday,
    icon: <CakeIcon fontSize="small" />,
  },
  nursery_event: {
    color: parentPortalTheme.eventTypes.nursery_event,
    icon: <CelebrationIcon fontSize="small" />,
  },
  graduation: {
    color: parentPortalTheme.eventTypes.graduation,
    icon: <GraduationIcon fontSize="small" />,
  },
  holiday: {
    color: parentPortalTheme.eventTypes.holiday,
    icon: <HolidayIcon fontSize="small" />,
  },
  field_trip: {
    color: parentPortalTheme.eventTypes.field_trip,
    icon: <FieldTripIcon fontSize="small" />,
  },
  special_event: {
    color: parentPortalTheme.eventTypes.special_event,
    icon: <StarIcon fontSize="small" />,
  },
};
const now = new Date();
const ParentEvents: React.FC = () => {
  const { t } = useTranslation("pages", { keyPrefix: "parentEvents" });
  const [events, setEvents] = useState<NurseryEvent[]>([]);
  const [selectedType, setSelectedType] = useState<NurseryEventType | null>(
    null,
  );
  const [selectedEvent, setSelectedEvent] = useState<NurseryEvent | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventTypes = useMemo(
    () => Object.keys(eventStyles) as NurseryEventType[],
    [],
  );
  const monthValue = `${year}-${String(month).padStart(2, "0")}`;
  const years = useMemo(
    () => Array.from({ length: 5 }, (_, index) => now.getFullYear() - index),
    [],
  );
  const loadEvents = useCallback(
    async (quiet = false) => {
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      try {
        const response = await parentPortalAPI.getEvents(
          monthValue,
          selectedType || undefined,
        );
        const list = response.events || response.data || [];
        setEvents(list);
        setPreviewMode(Boolean(response.preview_mode));
        setSelectedEvent((current) =>
          current
            ? (list.find((item) => item.id === current.id) ?? null)
            : null,
        );
      } catch (err: any) {
        if (!quiet) {
          setEvents([]);
          setPreviewMode(false);
          setError(
            err?.response?.data?.message || err?.message || t("loadFailed"),
          );
        } else console.warn("Parent event polling failed", err);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [monthValue, selectedType, t],
  );
  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);
  useEffect(() => {
    if (!selectedEvent || selectedEvent.auto_attach_status !== "in_progress")
      return;
    const timer = window.setInterval(() => {
      void loadEvents(true);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [selectedEvent?.id, selectedEvent?.auto_attach_status, loadEvents]);
  return (
    <ParentPortalLayout title={t("title")}>
      {previewMode && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 4,
            bgcolor: parentPortalTheme.preview.bannerBg,
            color: parentPortalTheme.preview.bannerColor,
          }}
        >
          {t("previewBanner")}
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>{t("month")}</InputLabel>
            <Select
              label={t("month")}
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, index) => index + 1).map(
                (value) => (
                  <MenuItem key={value} value={value}>
                    {new Date(2026, value - 1, 1).toLocaleString(undefined, {
                      month: "long",
                    })}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>{t("year")}</InputLabel>
            <Select
              label={t("year")}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
        {eventTypes.map((type) => (
          <Chip
            key={type}
            icon={eventStyles[type].icon as React.ReactElement}
            label={t(`types.${type}`)}
            onClick={() =>
              setSelectedType((current) => (current === type ? null : type))
            }
            sx={{
              bgcolor:
                selectedType === type
                  ? parentPortalTheme.primaryDark
                  : eventStyles[type].color,
              color:
                selectedType === type ? "#fff" : parentPortalTheme.text.primary,
              fontWeight: 900,
              borderRadius: 999,
            }}
          />
        ))}
      </Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ overflow: "hidden", borderRadius: 5 }}>
        <Table
          sx={{
            "& .MuiTableCell-root": { px: { xs: 2.5, sm: 3.5 } },
            "& .MuiTableCell-root:first-of-type": { pl: { xs: 3, sm: 4 } },
            "& .MuiTableCell-root:last-of-type": { pr: { xs: 3, sm: 4 } },
          }}
        >
          <TableHead
            sx={{
              bgcolor: parentPortalTheme.table.headerBg,
              "& .MuiTableCell-head": {
                bgcolor: parentPortalTheme.table.headerBg,
                color: "#fff",
              },
            }}
          >
            <TableRow>
              <TableCell>{t("date")}</TableCell>
              <TableCell>{t("eventName")}</TableCell>
              <TableCell>{t("videoSnapshots")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <ParentPortalTablePreloader columns={3} />
            ) : (
              events.map((event, index) => (
                <TableRow
                  key={event.id}
                  sx={{
                    bgcolor:
                      index % 2 ? parentPortalTheme.table.rowAlt : "#fff",
                  }}
                >
                  <TableCell>
                    {new Date(event.date).toLocaleDateString(undefined, {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        size="small"
                        label={t(`types.${event.type}`)}
                        sx={{
                          bgcolor: eventStyles[event.type].color,
                          fontWeight: 900,
                        }}
                      />
                      {event.title}
                      {event.auto_attach_status === "in_progress" && (
                        <Chip size="small" color="success" label={t("live")} />
                      )}
                    </Box>
                    {(event.start_time ||
                      event.child?.name ||
                      event.classroom?.name) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {[
                          event.start_time
                            ? `${event.start_time}${event.end_time ? `–${event.end_time}` : ""}`
                            : "",
                          event.child?.name,
                          event.classroom?.name,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      disabled={
                        !event.media?.length &&
                        event.auto_attach_status !== "in_progress"
                      }
                      onClick={() => setSelectedEvent(event)}
                      sx={parentPortalSolidButtonSx}
                    >
                      {t("viewSnapshots")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
            {!loading && !events.length && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  {t("noEvents")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      <Modal
        open={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
      >
        <Box
          sx={{
            position: "absolute",
            inset: { xs: 16, md: "6% 10%" },
            bgcolor: "#fff",
            borderRadius: 5,
            p: 3,
            overflow: "auto",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 2 }}>
            {selectedEvent?.title}
          </Typography>
          {selectedEvent?.auto_attach_status === "in_progress" &&
            !selectedEvent.media?.length && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("waitingForPhotos")}
              </Alert>
            )}
          <Grid container spacing={2}>
            {selectedEvent?.media?.map((media) => (
              <Grid key={media.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ position: "relative" }}>
                  {media.source && (
                    <Chip
                      size="small"
                      label={t(
                        media.source === "auto" ? "automatic" : "manual",
                      )}
                      sx={{ position: "absolute", zIndex: 1, m: 1 }}
                    />
                  )}
                  {media.media_type === "video" ? (
                    <video
                      src={media.url ?? `${import.meta.env.VITE_API_URL}/${media.file_path}`}
                      controls
                      style={{
                        width: "100%",
                        borderRadius: 16,
                        background: parentPortalTheme.primaryDark,
                      }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={media.url ?? `${import.meta.env.VITE_API_URL}/${media.file_path}`}
                      alt={media.original_filename}
                      onClick={() => setLightbox(media.url?? `${import.meta.env.VITE_API_URL}/${media.file_path}`)}
                      sx={{
                        width: "100%",
                        aspectRatio: "4/3",
                        objectFit: "cover",
                        borderRadius: 4,
                        cursor: "pointer",
                      }}
                    />
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Modal>
      <Modal open={Boolean(lightbox)} onClose={() => setLightbox(null)}>
        <Box
          sx={{
            position: "absolute",
            inset: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            aria-label={t("closeLightbox")}
            onClick={() => setLightbox(null)}
            sx={{
              position: "absolute",
              top: 0,
              insetInlineEnd: 0,
              zIndex: 1,
              bgcolor: "#fff",
              color: parentPortalTheme.primaryDark,
              boxShadow: 3,
              "&:hover": { bgcolor: "#f5f5f5" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={lightbox || ""}
            alt={t("mediaPreview")}
            sx={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 4 }}
          />
        </Box>
      </Modal>
    </ParentPortalLayout>
  );
};
export default ParentEvents;
